(function () {
  'use strict';
  if (window.SmagtronCartConsistency) return;
  const surfaceSelector = '[data-cart-consistency-surface]';
  const pendingSelector = '[data-cart-consistency-pending]';
  const fields = ['key', 'variant_id', 'product_id', 'product_title', 'quantity', 'original_price', 'final_price', 'original_line_price', 'final_line_price', 'image', 'url'];
  let running = false;
  // One bounded transaction per document; only a real navigation renews it.
  let used = false;
  let failed = false;
  let activeRequest;
  let locked = false;
  const previous = new Map();
  let generation = 0;
  let inFlight = 0;
  let notice;
  // Observe real cart writes from calculators and Dawn, including body consumption.
  // DOM generation remains a separate barrier: a settled response is not rendered HTML.
  const nativeFetch = window.fetch;
  window.fetch = function (input, options) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
    const tracked = url.origin === location.origin && (/\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname) || url.searchParams.has('section_id'));
    if (!tracked) return nativeFetch.apply(this, arguments);
    generation++;
    inFlight++;
    if (running) fail();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      inFlight--;
      generation++;
      // Request completion cannot renew the document transaction.
    };
    return nativeFetch.apply(this, arguments).then(response => {
      // Dawn cart-note intentionally ignores the response body. Drain a clone
      // without changing the caller's Response or waiting for a guessed delay.
      response.clone().arrayBuffer().then(finish, finish);
      return response;
    }, error => { finish(); throw error; });
  };

  function signature(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Cart data missing');
    const out = {};
    for (const key of ['currency', 'total_price', 'original_total_price', 'items_subtotal_price', 'item_count']) out[key] = cart[key];
    out.items = cart.items.map(item => Object.fromEntries(fields.map(key => [key, key === 'image' ? Boolean(item.image) : item[key]])));
    return JSON.stringify(out);
  }

  function block() {
    locked = true;
    if (!notice?.isConnected) {
      notice = document.createElement('div');
      notice.setAttribute('role', 'status');
      notice.dataset.cartConsistencyNotice = '';
      // The reload boundary must remain usable above the open native drawer,
      // not merely exist off-screen after the footer.
      notice.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:2147483647;max-height:50vh;overflow:auto;padding:12px 20px;background:#fff;color:#121212;border-bottom:2px solid #006090;box-shadow:0 2px 8px #0003;';
      const message = document.createElement('p');
      message.textContent = failed ? 'Dein Warenkorb konnte noch nicht bestätigt werden. Bitte lade ihn erneut. Checkout bleibt gesperrt.' : 'Dein Warenkorb wird geprüft. Checkout ist bis zur Bestätigung gesperrt.';
      const link = document.createElement('a');
      link.href = (window.Shopify?.routes?.root || '/') + 'cart';
      link.textContent = 'Warenkorb neu laden';
      link.className = 'button button--secondary';
      notice.append(message, link);
      document.body.append(notice);
    }
    // Also protect a healthy sibling surface while another surface is pending.
    document.querySelectorAll('[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons, .cart__dynamic-checkout-buttons, [name="updates[]"], .cart-quantity button, cart-remove-button').forEach(node => {
      if (!previous.has(node)) previous.set(node, { disabled: node.disabled, inert: node.inert, hidden: node.hidden });
      if ('disabled' in node) node.disabled = true;
      node.inert = true;
      if (node.matches('.additional-checkout-buttons, .cart__dynamic-checkout-buttons')) node.hidden = true;
    });
  }

  function unblock() {
    for (const [node, state] of previous) {
      if (node.isConnected) {
        if ('disabled' in node) node.disabled = state.disabled;
        node.inert = state.inert;
        node.hidden = state.hidden;
      }
    }
    previous.clear();
    locked = false;
    notice?.remove();
  }

  function fail() {
    if (failed) return;
    failed = true;
    activeRequest?.abort();
    notice?.remove();
    block();
    document.querySelectorAll('[data-cart-consistency-message]').forEach(node => {
      node.textContent = 'Dein Warenkorb konnte noch nicht vollständig bestätigt werden. Bitte lade ihn erneut. Der Checkout bleibt bis zur Bestätigung gesperrt.';
    });
  }

  async function json(url) {
    if (failed) throw new Error('Transaction closed');
    const controller = new AbortController();
    activeRequest = controller;
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) {
        const error = new Error('Cart response unavailable');
        error.stop = response.status === 429 || response.status === 403;
        throw error;
      }
      if (!/json|javascript/i.test(response.headers.get('content-type') || '')) {
        const error = new Error('Cart response is not JSON');
        error.stop = true;
        throw error;
      }
      return await response.json();
    } finally { clearTimeout(timer); activeRequest = null; }
  }

  async function recover() {
    if (running || failed || !document.querySelector(pendingSelector)) return;
    if (used || inFlight) { fail(); return; }
    used = true;
    running = true;
    block();
    const root = window.Shopify?.routes?.root || '/';
    const runGeneration = generation;
    try {
      for (let attempt = 0; attempt < 4; attempt++) {
        if (attempt) await new Promise(resolve => setTimeout(resolve, 750 * (2 ** (attempt - 1))));
        try {
          const targets = [...document.querySelectorAll(surfaceSelector)];
          if (failed || !targets.length || inFlight || generation !== runGeneration) { fail(); return; }
          const contents = targets.map(target => target.innerHTML);
          const before = await json(root + 'cart.js');
          // Stale sibling surfaces must not block fetching fresh native sections.
          if (failed || inFlight || generation !== runGeneration) { fail(); return; }
          const url = new URL(root + 'cart', location.origin);
          url.searchParams.set('sections', [...new Set(targets.map(node => node.dataset.section))].join(','));
          const sections = await json(url);
          const replacements = targets.map(target => {
            const text = sections[target.dataset.section];
            if (typeof text !== 'string') throw new Error('Section missing');
            const parsed = new DOMParser().parseFromString(text, 'text/html');
            const replacement = [...parsed.querySelectorAll(surfaceSelector)].find(node => node.dataset.cartConsistencySurface === target.dataset.cartConsistencySurface);
            if (!replacement || replacement.querySelector(pendingSelector)) throw new Error('Liquid still incomplete');
            const state = JSON.parse(replacement.querySelector('[data-cart-consistency-state]').textContent);
            if (signature(state) !== signature(before)) throw new Error('Liquid and Ajax disagree');
            return { target, replacement };
          });
          const after = await json(root + 'cart.js');
          if (failed || inFlight || generation !== runGeneration || targets.some((target, index) => !target.isConnected || target.innerHTML !== contents[index])) { fail(); return; }
          if (signature(before) !== signature(after)) { fail(); return; }
          // Use Shopify's native markup, including prices, discounts, properties and controls.
          replacements.forEach(({ target, replacement }) => target.replaceWith(replacement));
          document.querySelectorAll('cart-items, cart-drawer, cart-drawer-items, #main-cart-footer').forEach(node => node.classList.toggle('is-empty', after.item_count === 0));
          observer.takeRecords();
          unblock();
          document.dispatchEvent(new CustomEvent('smagtron:cart-confirmed'));
          return;
        } catch (error) {
          if (failed || inFlight || generation !== runGeneration || error.stop || attempt === 3) throw error;
        }
      }
    } catch (_) {
      fail();
    } finally {
      running = false;
      // Terminal failure and a spent success budget are absorbing for this document.
    }
  }

  document.addEventListener('submit', event => {
    if (locked && event.target.matches('#cart, #CartDrawer-Form')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('click', event => {
    if (locked && event.target.closest('[name="checkout"], a[href*="/checkout"], cart-remove-button, .cart-quantity button')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  const observer = new MutationObserver(records => {
    const changed = records.some(record => {
      if (record.target.closest?.('[data-cart-consistency-message], [data-cart-consistency-notice]')) return false;
      return record.target.closest?.(surfaceSelector) || [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches(surfaceSelector) || node.querySelector(surfaceSelector)));
    });
    if (changed) { generation++; if (running) fail(); }
    if (locked || document.querySelector(pendingSelector)) {
      block();
      if (!failed) recover();
    }
  });
  window.SmagtronCartConsistency = { signature };
  observer.observe(document.documentElement, { childList: true, subtree: true });
  recover();
})();
